import forEach from 'lodash/forEach'
import React, { FC } from 'react'

import styles from './Image.module.scss'
import { sizes, SizeType } from './imageSizes'

interface ImageProps {
  alt?: string
  auto?: 'webp'
  /**
  When simply cropping an image, use the query ?crop={width_value},{height_value}
  to crop the image from the center.
  The value can be in pixels (for example, ?crop=300,400)
  or in percentage (for example, ?crop=0.50,0.60)
  or a combination of both (for example, ?crop=300,0.60).
  Note that for the percentage value, you can also use the p parameter. For example, 100p for 100%.
  */
  crop?: string
  height?: number | string
  orient?: '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8'
  /**
  Controls the compression level of images that have Lossy file format.
  The value for this parameters can be entered in any whole number between 1 and 100.
  */
  quality?: number
  trim?: string
  type?: SizeType
  url?: string
  width?: number | string
}

type ImageURLParams = Pick<
ImageProps,
'auto' | 'crop' | 'height' | 'orient' | 'quality' | 'trim' | 'width'
> & { fit?: 'bounds' | 'cover' | 'crop' }

const offset = (crop: ImageProps['crop']): string | null => {
  if (crop === 'Top') {
    return '50'
  }
  if (crop === 'Bottom') {
    return '100'
  }
  return null
}

const generateImageURL = (
  params: ImageURLParams,
  url?: string,
): string => {
  if (!url) {
    return ''
  }
  const imageURL = new URL(url)
  forEach({ ...params, 'offset-y': offset(params.crop) }, (value, key) => {
    if (value) {
      imageURL.searchParams.set(key, value as any)
    }
  })
  return imageURL.toString()
}

const imageSources = ({
  auto,
  quality,
  type,
  width,
  ...params
}: ImageProps): string | undefined => {
  if (type) {
    return sizes[type].map(({ imageWidth }) => {
      const imageURL = generateImageURL(
        {
          auto,
          crop: params.crop,
          height: params.height,
          orient: params.orient,
          quality,
          trim: params.trim,
          width: imageWidth || width,
        },
        params.url,
      )
      return `${imageURL.toString()} ${imageWidth}w`
    }).join(', ')
  }
  return undefined
}

export const CSImage: FC<ImageProps> = ({
  alt,
  auto = 'webp',
  quality = 100,
  type,
  url,
  ...params
}: ImageProps) => {
  const sources = imageSources({
    ...params,
    auto,
    quality,
    type,
    url,
  })

  return (
    <div className={styles.imageWrapper}>
      <img
        alt={alt}
        src={
          sources
            ? url
            : generateImageURL(
              {
                ...params,
                auto,
                fit: 'crop',
                quality,
              },
              url,
            )
        }
        srcSet={sources}
        style={{ mixBlendMode: 'multiply' }}
      />
    </div>
  )
}
